import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  BookOpen, ChevronRight, ImagePlus, Loader2, Plus, Settings2, X, ExternalLink,
} from "lucide-react";
import { api, BACKEND_BASE } from "@/lib/api";
import EditionForm from "@/components/EditionForm";
import { longDate, settingsForm, statusLabel } from "@/lib/editionForm";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Digital editions (2026-10-19) — post a paper's online edition to
 * WordPress from its Adobe Publish Online or Flipsnack link + embed
 * code, with a cover image, then share it like any article.
 *
 * Each edition is an article document with kind "edition"
 * (backend/editions.py); this page lists them, starts a new one with a
 * suggested date / title / issue number, and holds the per-paper
 * settings. The edition itself opens at /editions/:id.
 */
export default function DigitalEditionsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cover, setCover] = useState(null);

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const { data: list, isLoading } = useQuery({ queryKey: ["editions"], queryFn: api.listEditions });
  const suggestQuery = useQuery({
    queryKey: ["edition-suggest"],
    queryFn: api.suggestEdition,
    enabled: creating,
    staleTime: 0,
  });
  const items = list?.items || [];
  const siteName = settings?.name || "";
  const noCategory = settings && !(settings.edition_settings_effective?.category_ids || []).length;

  const createMut = useMutation({
    mutationFn: async (values) => {
      if (!cover) throw new Error(tr("Choose the cover image first."));
      const art = await api.createEdition(values);
      try {
        await api.uploadArticleImage(art.id, cover);
      } catch (e) {
        toast.error(
          tr("Edition saved, but the cover didn't upload: {detail}. Add it on the next screen.", { detail: e?.response?.data?.detail || e.message }),
        );
      }
      return art;
    },
    onSuccess: (art) => {
      qc.invalidateQueries({ queryKey: ["editions"] });
      setCreating(false);
      setCover(null);
      navigate(`/editions/${art.id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || tr("Couldn't save the edition")),
  });

  const sug = suggestQuery.data;
  const sugNote = sug
    ? sug.source === "none"
      ? tr("No earlier edition found — check the date.")
      : tr("Last edition {longDate}{value}{value2} + {cadence_days} days.", { longDate: longDate(sug.last_edition_date), value: sug.last_issue_number ? tr(" (issue {last_issue_number})", { last_issue_number: sug.last_issue_number }) : "", value2: sug.source === "wordpress" ? tr(", found on the website") : "", cadence_days: sug.cadence_days })
    : "";

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-6 lg:p-10">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <BookOpen className="w-6 h-6 text-[var(--accent)]" />
            <h1 className="font-serif-display text-3xl">{tr("Digital editions")}</h1>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                data-testid="editions-settings-toggle"
                onClick={() => setShowSettings((s) => !s)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
              >
                <Settings2 className="w-3.5 h-3.5" />{" "}{tr("Settings")}</button>
              {!creating && (
                <button
                  type="button"
                  data-testid="editions-new"
                  onClick={() => setCreating(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
                >
                  <Plus className="w-3.5 h-3.5" />{" "}{tr("New edition")}</button>
              )}
            </div>
          </div>
          <p className="text-sm text-[var(--ink-muted)] mb-6">{tr("Post the online edition to the website from its link and embed code, then share it like an article.")}</p>

          {noCategory && (
            <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 text-sm text-amber-900">{tr("No edition category is set for this paper yet, so posts would land in WordPress's default category. Pick it under")}{" "}<button type="button" className="underline" onClick={() => setShowSettings(true)}>{tr("Settings")}</button>.
            </div>
          )}

          {showSettings && (
            <EditionSettings settings={settings} onClose={() => setShowSettings(false)} />
          )}

          {creating && (
            <div className="bg-white border border-[var(--border)] p-5 mb-6" data-testid="edition-new-card">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="font-serif-display text-xl">{tr("New edition")}</h2>
                <button
                  type="button"
                  onClick={() => {
                    setCreating(false);
                    setCover(null);
                  }}
                  className="ml-auto text-[var(--ink-muted)] hover:text-[var(--ink)]"
                  title={tr("Cancel")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {suggestQuery.isLoading || !sug ? (
                <div className="py-6 flex items-center gap-2 text-sm text-[var(--ink-muted)]">
                  <Loader2 className="w-4 h-4 animate-spin" />{" "}{tr("Working out the next edition…")}</div>
              ) : (
                <>
                  <p className="text-[11px] text-[var(--ink-muted)] mb-4" data-testid="edition-suggest-note">{sugNote}</p>
                  <EditionForm
                    key={sug.edition_date + (sug.issue_number || "")}
                    initial={{
                      edition_date: sug.edition_date,
                      title: sug.title,
                      issue_number: sug.issue_number,
                    }}
                    settings={settings}
                    siteName={siteName}
                    busy={createMut.isPending}
                    submitLabel={tr("Save edition")}
                    onSubmit={(v) => createMut.mutate(v)}
                    testId="edition-new"
                    extra={<CoverPicker file={cover} onChange={setCover} />}
                  />
                </>
              )}
            </div>
          )}

          <div className="bg-white border border-[var(--border)]">
            <div className="px-5 py-3 border-b border-[var(--border)] overline">{tr("Editions ({itemCount})", { itemCount: items.length })}</div>
            {isLoading ? (
              <div className="p-6 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-[var(--ink-muted)]" />
              </div>
            ) : items.length === 0 ? (
              <div className="p-6 text-sm text-[var(--ink-muted)]">{tr("No editions posted from Newsdesk yet.")}</div>
            ) : (
              <ul>
                {items.map((r) => (
                  <li key={r.id} className="border-b border-[var(--border)] last:border-b-0">
                    <Link
                      to={`/editions/${r.id}`}
                      data-testid={`edition-row-${r.id}`}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-neutral-50"
                    >
                      {r.generated_image_url ? (
                        <img src={fileSrc(r.generated_image_url)} alt="" className="w-14 h-10 object-cover border border-[var(--border)]" />
                      ) : (
                        <div className="w-14 h-10 border border-dashed border-[var(--border)]" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[var(--ink)] truncate">{r.headline}</div>
                        <div className="text-[11px] text-[var(--ink-muted)]">
                          {r.issue_number ? tr("Issue {issue_number} · ", { issue_number: r.issue_number }) : ""}
                          {r.platform === "flipsnack" ? tr("Flipsnack") : r.platform === "adobe" ? tr("Adobe Publish Online") : ""}
                        </div>
                      </div>
                      <StatusPill status={r.status} />
                      {r.wordpress_link && (
                        <button
                          type="button"
                          onClick={(e) => {
                            // A link can't sit inside the row's link.
                            e.preventDefault();
                            e.stopPropagation();
                            window.open(r.wordpress_link, "_blank", "noopener");
                          }}
                          className="text-[var(--ink-muted)] hover:text-[var(--ink)]"
                          title={tr("Open on the website")}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <ChevronRight className="w-4 h-4 text-[var(--ink-muted)]" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Stored images are "/api/files/…" paths on the backend. */
export function fileSrc(url) {
  return url && url.startsWith("/") ? `${BACKEND_BASE}${url}` : url || "";
}

export function StatusPill({ status }) {
  const cls =
    status === "published_live"
      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
      : status === "scheduled"
        ? "bg-amber-50 border-amber-200 text-amber-900"
        : "bg-white border-[var(--border)] text-[var(--ink-muted)]";
  return (
    <span className={`text-[10px] uppercase tracking-wider font-mono-display px-2 py-0.5 border ${cls}`}>
      {statusLabel(status)}
    </span>
  );
}

export function CoverPicker({ file, onChange, label = tr("Cover image") }) {
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);
  return (
    <div>
      <div className="overline flex items-center gap-1.5 mb-1.5">
        <ImagePlus className="w-3 h-3" /> {label}
      </div>
      <div className="flex items-center gap-3">
        {preview && <img src={preview} alt="" className="w-24 h-16 object-cover border border-[var(--border)]" />}
        <label className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-[var(--border)] hover:border-[var(--ink)] cursor-pointer">
          <ImagePlus className="w-4 h-4" />
          {file ? tr("Choose another") : tr("Choose image")}
          <input
            type="file"
            accept="image/*"
            data-testid="edition-cover-input"
            className="hidden"
            onChange={(e) => onChange(e.target.files?.[0] || null)}
          />
        </label>
        {trRich("<0>The featured image on the website, social posts and the newsletter.</0>", null, [<span className="text-[11px] text-[var(--ink-muted)]" />])}
      </div>
    </div>
  );
}

function EditionSettings({ settings, onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState(() => settingsForm(settings));
  const [catFilter, setCatFilter] = useState("");
  const cats = settings?.wordpress_categories || [];
  const authors = settings?.wordpress_authors || [];
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleCat = (id) =>
    set("category_ids", f.category_ids.includes(id) ? f.category_ids.filter((x) => x !== id) : [...f.category_ids, id]);
  const shownCats = cats.filter((c) => (c.name || "").toLowerCase().includes(catFilter.trim().toLowerCase()));

  const save = useMutation({
    mutationFn: () => api.updateSettings({ edition_settings: { ...f, cadence_days: Number(f.cadence_days) || 14 } }),
    onSuccess: () => {
      toast.success(tr("Edition settings saved"));
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["edition-suggest"] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Save failed")),
  });

  const inputCls =
    "w-full px-3 py-2 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]";

  return (
    <div className="bg-white border border-[var(--border)] p-5 mb-6 space-y-4" data-testid="edition-settings">
      <div className="flex items-center gap-2">
        <h2 className="font-serif-display text-xl">{tr("Edition settings · {name}", { name: settings?.name })}</h2>
        <button type="button" onClick={onClose} className="ml-auto text-[var(--ink-muted)] hover:text-[var(--ink)]" title={tr("Close")}>
          <X className="w-4 h-4" />
        </button>
      </div>

      <div>
        <div className="overline mb-1.5">{tr("WordPress categories")}</div>
        {cats.length === 0 ? (
          <p className="text-sm text-[var(--ink-muted)]">{tr("No categories loaded yet.")}{" "}<Link to="/settings" className="text-[var(--accent)] underline">{tr("Settings → Sync from WordPress")}</Link>{" "}{tr("first.")}</p>
        ) : (
          <>
            <input
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              placeholder={tr("Filter categories…")}
              className={inputCls + " mb-2"}
            />
            <div className="max-h-40 overflow-auto border border-[var(--border)] p-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
              {shownCats.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={f.category_ids.includes(c.id)}
                    onChange={() => toggleCat(c.id)}
                  />
                  {c.name}
                </label>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="overline mb-1.5 block" htmlFor="edset-cadence">{tr("Days between editions")}</label>
          <input
            id="edset-cadence"
            data-testid="edition-settings-cadence"
            type="number"
            min={1}
            max={90}
            value={f.cadence_days}
            onChange={(e) => set("cadence_days", e.target.value)}
            className={inputCls}
          />
          <p className="mt-1 text-[11px] text-[var(--ink-muted)]">{tr("14 for every two weeks, 7 for weekly. Used to suggest the next date.")}</p>
        </div>
        <div>
          <label className="overline mb-1.5 block" htmlFor="edset-author">{tr("WordPress author")}</label>
          <select
            id="edset-author"
            value={f.author_id ?? ""}
            onChange={(e) => set("author_id", e.target.value ? Number(e.target.value) : null)}
            className={inputCls}
          >
            <option value="">{tr("The account Newsdesk posts with")}</option>
            {authors.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
      </div>

      <TextSetting id="title_template" label={tr("Post title")} hint={tr("{date} = October 19, 2026 · {site} · {issue}")} f={f} set={set} cls={inputCls} />
      <TextSetting id="link_text" label={tr("Full-screen link text")} f={f} set={set} cls={inputCls} />
      <TextSetting id="text_after" label={tr("Text under the edition (optional)")} hint={tr("One paragraph per line.")} f={f} set={set} cls={inputCls} rows={2} />
      <TextSetting id="excerpt_template" label={tr("Excerpt")} f={f} set={set} cls={inputCls} />
      <TextSetting id="facebook_template" label={tr("Starting Facebook caption")} f={f} set={set} cls={inputCls} rows={2} />
      <TextSetting id="instagram_template" label={tr("Starting Instagram caption")} f={f} set={set} cls={inputCls} rows={2} />

      <div className="flex justify-end">
        <button
          type="button"
          data-testid="edition-settings-save"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-60"
        >
          {save.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{tr("Save settings")}</button>
      </div>
    </div>
  );
}

function TextSetting({ id, label, hint, f, set, cls, rows }) {
  return (
    <div>
      <label className="overline mb-1.5 block" htmlFor={`edset-${id}`}>{label}</label>
      {rows ? (
        <textarea id={`edset-${id}`} rows={rows} value={f[id]} onChange={(e) => set(id, e.target.value)} className={cls} />
      ) : (
        <input id={`edset-${id}`} value={f[id]} onChange={(e) => set(id, e.target.value)} className={cls} />
      )}
      {hint && <p className="mt-1 text-[11px] text-[var(--ink-muted)]">{hint}</p>}
    </div>
  );
}
